import { useContext, useEffect, createContext } from 'react'
import { useStateWithMerge } from 'hooks'
import API from 'config/api'
import { CONFIG_CATEGORIES, CONFIG_KEYS } from 'utils/configurations'

const ConfigContext = createContext(null)

interface ConfigurationResponse {
  unit: {
    id: number
    name: string
    divisionId: number
    corporationId: number
  }
  configurations: {
    [category: string]: {
      [key: string]: unknown
    }
  }
}

function isConfigurationResponse(data: unknown): data is ConfigurationResponse {
  return (
    typeof data === `object` &&
    data !== null &&
    `unit` in data &&
    `configurations` in data
  )
}

interface ConfigState {
  admitSocialName: boolean
  tagsFilterEnabled: boolean
  corporationId: number | null
  isLoading: boolean
}

const getInitialState = (): ConfigState => ({
  admitSocialName: false,
  tagsFilterEnabled: false,
  corporationId: null,
  isLoading: false,
})

const emptyConfig = {
  admitSocialName: false,
  tagsFilterEnabled: false,
  corporationId: null,
}

// Las configuraciones viajan como JSON, asi que un booleano puede llegar
// guardado como string. `Boolean('false')` seria true, de ahi la comparacion.
function toBoolean(value: unknown): boolean {
  return value === true || value === `true`
}

interface ConfigProviderProps {
  children: React.ReactNode
  unitId: string | number
}

function ConfigProvider({
  children,
  unitId,
}: ConfigProviderProps): JSX.Element {
  const [state, setState] = useStateWithMerge(getInitialState())

  useEffect(() => {
    // La unidad puede cambiar antes de que llegue la respuesta anterior, y
    // `corporationId` define que catalogo de etiquetas se pide: una respuesta
    // vieja dejaria las pildoras de otra corporacion en pantalla.
    let isCurrent = true

    async function fetchConfigurations(): Promise<void> {
      if (!unitId || unitId === 0) {
        setState({ ...emptyConfig, isLoading: false })
        return
      }

      setState({ isLoading: true })
      try {
        const data = await API.getConfigurations(unitId)

        if (!isCurrent) {
          return
        }

        if (!isConfigurationResponse(data)) {
          setState({ ...emptyConfig, isLoading: false })
          return
        }

        // Extract admitSocialName from the configurations object
        const pacienteCategory = CONFIG_CATEGORIES.PACIENTE
        const pacienteConfig = data.configurations?.[pacienteCategory]
        const admitSocialNameKey = CONFIG_KEYS.PACIENTE.ADMIT_SOCIAL_NAME
        const admitSocialNameValue = pacienteConfig?.[admitSocialNameKey]
        const admitSocialName = Boolean(admitSocialNameValue ?? false)

        // Filtro por etiquetas del Monitor de Sala, normalmente definido a
        // nivel de corporacion y heredado hacia divisiones y unidades.
        const monitorConfig = data.configurations?.[CONFIG_CATEGORIES.MONITOR]
        const tagsFilterEnabled = toBoolean(
          monitorConfig?.[CONFIG_KEYS.MONITOR.TAGS_FILTER_ENABLED],
        )

        setState({
          admitSocialName,
          tagsFilterEnabled,
          corporationId: data.unit?.corporationId ?? null,
          isLoading: false,
        })
      } catch (e) {
        // If configuration fetch fails, default to false
        if (isCurrent) {
          setState({ ...emptyConfig, isLoading: false })
        }
      }
    }

    fetchConfigurations()
    return () => {
      isCurrent = false
    }
  }, [unitId])

  const { admitSocialName, tagsFilterEnabled, corporationId, isLoading } = state

  return (
    <ConfigContext.Provider
      value={{ admitSocialName, tagsFilterEnabled, corporationId, isLoading }}
    >
      {children}
    </ConfigContext.Provider>
  )
}

interface ConfigContextType {
  admitSocialName: boolean
  tagsFilterEnabled: boolean
  corporationId: number | null
  isLoading: boolean
}

function useConfig(): ConfigContextType {
  const context = useContext(ConfigContext)
  if (!context) {
    if (process.env.NODE_ENV !== `production`) {
      console.warn(
        `useConfig() is being used outside of ConfigProvider. ` +
          `Returning default values. If this is intentional (e.g., in modals), ` +
          `consider passing the config values as props instead.`,
      )
    }
    // Return default values when used outside ConfigProvider (e.g., in modals)
    return { ...emptyConfig, isLoading: false }
  }
  return context
}

export { ConfigProvider, useConfig }
